import { Skeleton } from '@/components/ui/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { useSummary } from '@/lib/items'

function Stat({
  label,
  value,
  hint,
  testId,
  compare,
}: {
  label: string
  value: number
  hint: string
  testId: string
  compare?: string
}) {
  return (
    <Tooltip>
      <TooltipTrigger
        render={
          <button
            type="button"
            className="flex min-w-0 flex-col items-start rounded-lg px-3 py-2 text-left outline-none hover:bg-muted/50 focus-visible:ring-3 focus-visible:ring-ring/50"
          >
            <span className="text-xs text-muted-foreground">{label}</span>
            <span className="font-heading text-2xl font-semibold tabular-nums" data-testid={testId}>
              {value}
            </span>
            {compare ? (
              <span className="truncate text-xs text-muted-foreground">{compare}</span>
            ) : null}
          </button>
        }
      />
      <TooltipContent className="max-w-60">{hint}</TooltipContent>
    </Tooltip>
  )
}

/** This week at a glance: came in, done, open now (insights I0). */
export function WeekStrip() {
  const summary = useSummary()
  if (summary.isPending) {
    return <Skeleton className="h-20 w-full rounded-xl" data-testid="week-strip-loading" />
  }
  if (!summary.data) return null
  const s = summary.data
  return (
    <section
      aria-label={`This week, ${s.period.label}`}
      data-testid="week-strip"
      className="grid grid-cols-3 gap-1 rounded-xl border p-1"
    >
      <Stat
        label="Came in"
        value={s.cameIn}
        testId="week-in"
        compare={`${s.previous.cameIn} last week`}
        hint="Items first seen this week: captured by you or received from others."
      />
      <Stat
        label="Done"
        value={s.done}
        testId="week-done"
        compare={`${s.previous.done} last week`}
        hint="Items marked done this week. Dropped and FYI items do not count."
      />
      <Stat
        label="Open"
        value={s.open}
        testId="week-open"
        compare="right now"
        hint="Everything not done or dropped yet: inbox, open and snoozed items."
      />
    </section>
  )
}
